import { supabase } from "@/integrations/supabase/client";

/**
 * Safe usage tracking service that uses the existing increment_organization_counter function
 * This service is designed to be non-disruptive and fail silently if there are errors
 */
export class SafeUsageTracking {
  /**
   * Safely increments an organization counter without throwing errors
   */
  private static async safeIncrement(
    organizationId: string,
    counterName: string,
    incrementBy: number = 1,
    context?: string,
  ): Promise<void> {
    try {
      // Use the existing increment_organization_counter function
      const { error } = await supabase.rpc("increment_organization_counter", {
        org_id: organizationId,
        counter_name: counterName,
        increment_by: incrementBy,
      });

      if (error) {
        console.warn(
          `[SafeUsageTracking] Failed to increment ${counterName} for org ${organizationId}:`,
          error.message,
        );
        return;
      }

      console.log(
        `[SafeUsageTracking] ✅ Incremented ${counterName} by ${incrementBy} for org ${organizationId}${context ? ` (${context})` : ""}`,
      );
    } catch (error) {
      // Fail silently to not disrupt user experience
      console.warn(
        `[SafeUsageTracking] Error incrementing ${counterName}:`,
        error,
      );
    }
  }

  /**
   * Track when a conversation is started
   */
  static async trackConversationStarted(
    organizationId: string,
    agentId?: string,
  ): Promise<void> {
    await this.safeIncrement(
      organizationId,
      "conversations_this_month",
      1,
      `conversation started${agentId ? ` for agent ${agentId}` : ""}`,
    );
  }

  /**
   * Track when AI content is generated (AI assistants)
   */
  static async trackAIGeneration(
    organizationId: string,
    assistantId?: string,
  ): Promise<void> {
    await this.safeIncrement(
      organizationId,
      "ai_generations_this_month",
      1,
      `AI generation${assistantId ? ` for assistant ${assistantId}` : ""}`,
    );
  }

  /**
   * Track voice call usage
   */
  static async trackVoiceCall(
    organizationId: string,
    voiceAgentId?: string,
  ): Promise<void> {
    await this.safeIncrement(
      organizationId,
      "voice_calls_this_month",
      1,
      `voice call${voiceAgentId ? ` for voice agent ${voiceAgentId}` : ""}`,
    );
  }

  /**
   * Track voice minutes used
   */
  static async trackVoiceMinutes(
    organizationId: string,
    minutes: number,
    voiceAgentId?: string,
  ): Promise<void> {
    await this.safeIncrement(
      organizationId,
      "voice_minutes_used_this_month",
      Math.ceil(minutes), // Round up to nearest minute
      `${minutes} voice minutes${voiceAgentId ? ` for voice agent ${voiceAgentId}` : ""}`,
    );
  }

  /**
   * Track API call usage
   */
  static async trackAPICall(
    organizationId: string,
    endpoint?: string,
  ): Promise<void> {
    await this.safeIncrement(
      organizationId,
      "api_calls_this_month",
      1,
      `API call${endpoint ? ` to ${endpoint}` : ""}`,
    );
  }

  /**
   * Track when an agent is activated (increment active agents count)
   */
  static async trackAgentActivated(
    organizationId: string,
    agentId: string,
  ): Promise<void> {
    await this.safeIncrement(
      organizationId,
      "active_agents_count",
      1,
      `agent ${agentId} activated`,
    );
  }

  /**
   * Track when an agent is deactivated (decrement active agents count)
   */
  static async trackAgentDeactivated(
    organizationId: string,
    agentId: string,
  ): Promise<void> {
    await this.safeIncrement(
      organizationId,
      "active_agents_count",
      -1,
      `agent ${agentId} deactivated`,
    );
  }

  /**
   * Track when a team member joins (increment team members count)
   */
  static async trackTeamMemberAdded(
    organizationId: string,
    userId?: string,
  ): Promise<void> {
    await this.safeIncrement(
      organizationId,
      "team_members_count",
      1,
      `team member${userId ? ` ${userId}` : ""} added`,
    );
  }

  /**
   * Track when a team member leaves (decrement team members count)
   */
  static async trackTeamMemberRemoved(
    organizationId: string,
    userId?: string,
  ): Promise<void> {
    await this.safeIncrement(
      organizationId,
      "team_members_count",
      -1,
      `team member${userId ? ` ${userId}` : ""} removed`,
    );
  }

  /**
   * Helper function to get organization ID from agent ID
   */
  static async getOrganizationIdFromAgent(
    agentId: string,
  ): Promise<string | null> {
    try {
      const { data, error } = await supabase
        .from("agents")
        .select("organization_id")
        .eq("id", agentId)
        .single();

      if (error || !data) {
        console.warn(
          `[SafeUsageTracking] Could not find organization for agent ${agentId}:`,
          error?.message,
        );
        return null;
      }

      return data.organization_id;
    } catch (error) {
      console.warn(
        `[SafeUsageTracking] Error getting organization for agent ${agentId}:`,
        error,
      );
      return null;
    }
  }

  /**
   * Helper function to get organization ID from voice agent ID
   */
  static async getOrganizationIdFromVoiceAgent(
    voiceAgentId: string,
  ): Promise<string | null> {
    try {
      const { data, error } = await supabase
        .from("voice_agents")
        .select("organization_id")
        .eq("id", voiceAgentId)
        .single();

      if (error || !data) {
        console.warn(
          `[SafeUsageTracking] Could not find organization for voice agent ${voiceAgentId}:`,
          error?.message,
        );
        return null;
      }

      return data.organization_id;
    } catch (error) {
      console.warn(
        `[SafeUsageTracking] Error getting organization for voice agent ${voiceAgentId}:`,
        error,
      );
      return null;
    }
  }

  /**
   * Helper function to get organization ID from AI assistant ID
   */
  static async getOrganizationIdFromAssistant(
    assistantId: string,
  ): Promise<string | null> {
    try {
      const { data, error } = await supabase
        .from("ai_assistants")
        .select("owner_organization_id")
        .eq("id", assistantId)
        .single();

      if (error || !data) {
        console.warn(
          `[SafeUsageTracking] Could not find organization for assistant ${assistantId}:`,
          error?.message,
        );
        return null;
      }

      return data.owner_organization_id;
    } catch (error) {
      console.warn(
        `[SafeUsageTracking] Error getting organization for assistant ${assistantId}:`,
        error,
      );
      return null;
    }
  }

  /**
   * Track conversation with automatic organization lookup
   */
  static async trackConversationForAgent(agentId: string): Promise<void> {
    const organizationId = await this.getOrganizationIdFromAgent(agentId);
    if (organizationId) {
      await this.trackConversationStarted(organizationId, agentId);
    }
  }

  /**
   * Track AI generation with automatic organization lookup
   */
  static async trackAIGenerationForAssistant(
    assistantId: string,
  ): Promise<void> {
    const organizationId =
      await this.getOrganizationIdFromAssistant(assistantId);
    if (organizationId) {
      await this.trackAIGeneration(organizationId, assistantId);
    }
  }

  /**
   * Track voice call with automatic organization lookup
   */
  static async trackVoiceCallForAgent(voiceAgentId: string): Promise<void> {
    const organizationId =
      await this.getOrganizationIdFromVoiceAgent(voiceAgentId);
    if (organizationId) {
      await this.trackVoiceCall(organizationId, voiceAgentId);
    }
  }

  /**
   * Track voice minutes with automatic organization lookup
   */
  static async trackVoiceMinutesForAgent(
    voiceAgentId: string,
    minutes: number,
  ): Promise<void> {
    const organizationId =
      await this.getOrganizationIdFromVoiceAgent(voiceAgentId);
    if (organizationId) {
      await this.trackVoiceMinutes(organizationId, minutes, voiceAgentId);
    }
  }
}

// Export convenience functions for easier imports
/**
 * Recalculate and update the actual count of active agents for an organization
 * This is more reliable than increments/decrements for real-time counts
 */
export const trackActiveAgents = async (
  organizationId: string,
): Promise<void> => {
  try {
    if (!organizationId) {
      console.warn(
        "[SafeUsageTracking] No organization ID provided for active agents tracking",
      );
      return;
    }

    // Get current count of active agents for this organization
    const { data: activeAgents, error } = await supabase
      .from("agents")
      .select("id")
      .eq("organization_id", organizationId)
      .eq("is_active", true);

    if (error) {
      console.warn(
        "[SafeUsageTracking] Failed to query active agents:",
        error.message,
      );
      return;
    }

    const activeCount = activeAgents?.length || 0;

    // Update the organization's active agents count directly
    const { error: updateError } = await supabase
      .from("organizations")
      .update({ active_agents_count: activeCount })
      .eq("id", organizationId);

    if (updateError) {
      console.warn(
        "[SafeUsageTracking] Failed to update active agents count:",
        updateError.message,
      );
      return;
    }

    console.log(
      `[SafeUsageTracking] ✅ Updated active agents count to ${activeCount} for org ${organizationId}`,
    );
  } catch (error) {
    console.warn("[SafeUsageTracking] Error tracking active agents:", error);
  }
};

/**
 * Calculate and update the actual storage usage for an organization
 * This involves summing up file sizes from various sources
 */
export const trackStorageUsage = async (
  organizationId: string,
): Promise<void> => {
  try {
    if (!organizationId) {
      console.warn("[Storage Tracking] No organization ID provided");
      return;
    }

    let totalStorageMB = 0;

    // 1. Calculate storage from agent_files bucket (uploaded files)
    try {
      const { data: files, error: filesError } = await supabase.storage
        .from("agent_files")
        .list("", {
          limit: 1000, // Adjust if organizations have more files
          offset: 0,
        });

      if (filesError) {
        console.warn(
          "[Storage Tracking] Failed to list agent files:",
          filesError.message,
        );
      } else if (files) {
        // Sum up file sizes (files include metadata with size)
        const agentFilesSize = files.reduce((total, file) => {
          return total + (file.metadata?.size || 0);
        }, 0);
        totalStorageMB += Math.ceil(agentFilesSize / (1024 * 1024)); // Convert bytes to MB, round up
      }
    } catch (error) {
      console.warn(
        "[Storage Tracking] Error calculating agent files storage:",
        error,
      );
    }

    // 2. Calculate storage from database tables (conversations, AI outputs, etc.)
    try {
      // Estimate storage from text content in conversations
      const { data: conversations, error: convoError } = await supabase
        .from("conversations")
        .select("id, messages")
        .eq("organization_id", organizationId);

      if (!convoError && conversations) {
        const conversationStorageBytes = conversations.reduce(
          (total, convo) => {
            // Estimate storage: messages JSON + metadata
            const messagesSize = JSON.stringify(convo.messages || []).length;
            return total + messagesSize + 500; // +500 bytes for metadata
          },
          0,
        );
        totalStorageMB += Math.ceil(conversationStorageBytes / (1024 * 1024));
      }

      // Estimate storage from AI outputs
      const { data: aiOutputs, error: aiError } = await supabase
        .from("ai_outputs")
        .select("structured_output, input_data")
        .eq("organization_id", organizationId);

      if (!aiError && aiOutputs) {
        const aiOutputStorageBytes = aiOutputs.reduce((total, output) => {
          const outputSize = JSON.stringify(
            output.structured_output || {},
          ).length;
          const inputSize = JSON.stringify(output.input_data || {}).length;
          return total + outputSize + inputSize + 200; // +200 bytes for metadata
        }, 0);
        totalStorageMB += Math.ceil(aiOutputStorageBytes / (1024 * 1024));
      }
    } catch (error) {
      console.warn(
        "[Storage Tracking] Error calculating database storage:",
        error,
      );
    }

    // 3. Update the organization's storage usage
    const { error: updateError } = await supabase
      .from("organizations")
      .update({ storage_usage_mb: totalStorageMB })
      .eq("id", organizationId);

    if (updateError) {
      console.warn(
        "[Storage Tracking] Failed to update storage usage:",
        updateError.message,
      );
      return;
    }

    console.log(
      `[Storage Tracking] ✅ Updated storage usage to ${totalStorageMB}MB for org ${organizationId}`,
    );
  } catch (error) {
    console.warn("[Storage Tracking] Error:", error);
  }
};

// Export convenience functions for easier imports
export const trackConversation =
  SafeUsageTracking.trackConversationForAgent.bind(SafeUsageTracking);
export const trackAIGeneration =
  SafeUsageTracking.trackAIGenerationForAssistant.bind(SafeUsageTracking);
export const trackVoiceCall =
  SafeUsageTracking.trackVoiceCallForAgent.bind(SafeUsageTracking);
export const trackVoiceMinutes =
  SafeUsageTracking.trackVoiceMinutesForAgent.bind(SafeUsageTracking);
export const trackAPICall =
  SafeUsageTracking.trackAPICall.bind(SafeUsageTracking);
export const trackAgentActivated =
  SafeUsageTracking.trackAgentActivated.bind(SafeUsageTracking);
export const trackAgentDeactivated =
  SafeUsageTracking.trackAgentDeactivated.bind(SafeUsageTracking);
